import styled from "styled-components";
import {
    LeftSection,
    SectionHeader,
    SectionTitle,
    SectionHeaderGroup,
    ToggleContainer,
    ToggleLabel,
    Card,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    ResetAllButton,
    IconPickerGrid,
} from "../shared/styled";

// ─── Layout ──────────────────────────────────────────────

export const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 20px;
    height: 100%;

    @media (max-width: 1200px) {
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 576px) {
        padding: 10px;
        gap: 15px;
    }
`;

// ─── Header & Controls ──────────────────────────────────

export const Toggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 50px;
    height: 26px;
    border-radius: 13px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${({ isActive }) => (isActive ? "27px" : "3px")};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const SmallToggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 40px;
    height: 22px;
    border-radius: 11px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: ${({ isActive }) => (isActive ? "20px" : "2px")};
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const VisibilityButton = styled.button<{ isVisible: boolean }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ isVisible }) => (isVisible ? "#666" : "#ccc")};
    transition: all 0.2s ease;

    &:hover {
        color: #333;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

// ─── Card ────────────────────────────────────────────────

// ─── Language Tabs ───────────────────────────────────────

export const LanguageTabs = styled.div`
    display: flex;
    gap: 5px;
    margin-bottom: 15px;
    flex-wrap: wrap;

    @media (max-width: 576px) {
        gap: 8px;
        justify-content: center;
    }
`;

export const LanguageTab = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    padding: 6px 16px;
    border: 1px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    border-radius: 20px;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#fff"};
    color: ${({ isActive }) => (isActive ? "#fff" : "#333")};
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

// ─── Input ───────────────────────────────────────────────

export const ResetButton = styled.button<{ mainColor: string }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ mainColor }) => mainColor};
    transition: background-color 0.2s ease;
    border-radius: 4px;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}20`};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

// ─── Action Config Card ─────────────────────────────────

export const ActionConfigCard = styled.div<{ isActive: boolean }>`
    background-color: ${({ isActive }) => (isActive ? "#fff" : "#fafafa")};
    border: 1px solid ${({ isActive }) => (isActive ? "#e0e0e0" : "#eee")};
    border-radius: 10px;
    padding: 16px;
    opacity: ${({ isActive }) => (isActive ? 1 : 0.6)};
    transition: all 0.2s ease;
`;

export const ActionConfigHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

export const ActionConfigTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    font-size: 14px;
    color: #333;
`;

export const ActionConfigControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const ActionConfigBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const ActionFieldRow = styled.div`
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 10px;
    align-items: center;

    @media (max-width: 576px) {
        grid-template-columns: 1fr;
        gap: 6px;
    }
`;

export const ActionTextRow = styled.div`
    display: grid;
    grid-template-columns: 100px 1fr 2fr;
    gap: 10px;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    @media (max-width: 768px) {
        grid-template-columns: 80px 1fr 1.5fr;
        gap: 8px;
        padding: 8px 0;
    }

    @media (max-width: 576px) {
        grid-template-columns: 1fr;
        gap: 10px;
        padding: 15px 0;
    }
`;

export const IconCircle = styled.div<{ bgColor: string; size?: number }>`
    width: ${({ size }) => size || 36}px;
    height: ${({ size }) => size || 36}px;
    border-radius: 50%;
    background-color: ${({ bgColor }) => bgColor};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
        width: ${({ size }) => (size ? size * 0.55 : 20)}px;
        height: ${({ size }) => (size ? size * 0.55 : 20)}px;
        fill: white;
    }
`;

export const IconPickerWrapper = styled.div`
    position: relative;
`;

export const IconPickerButton = styled.button<{ mainColor: string }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font-size: 12px;
    color: #333;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

export const IconPickerDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 100;
    min-width: 280px;
`;

export const IconPickerItem = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 2px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    background-color: ${({ isActive, mainColor }) =>
        isActive ? `${mainColor}15` : "#fff"};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    svg {
        width: 22px;
        height: 22px;
        fill: ${({ isActive, mainColor }) => (isActive ? mainColor : "#666")};
    }

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

export const ColorPickerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
`;

export const ColorInput = styled.input`
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    padding: 0;

    &::-webkit-color-swatch-wrapper {
        padding: 2px;
    }

    &::-webkit-color-swatch {
        border: 1px solid #ddd;
        border-radius: 4px;
    }
`;

export const ColorLabel = styled.span`
    font-size: 11px;
    color: #999;
`;

// ─── Preview Mobile ──────────────────────────────────────

// Shared layout primitives (see ../shared/styled).
export {
    LeftSection,
    SectionHeader,
    SectionTitle,
    SectionHeaderGroup as ActionsHeaderGroup,
    ToggleContainer,
    ToggleLabel,
    Card,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    ResetAllButton,
    IconPickerGrid,
};
